/**
 * Integration catalog types used by `GET /pay/org/:orgId/integrations`.
 *
 * The endpoint returns a list of supported third-party integrations and their
 * organization-specific activation status. The catalog is assembled in
 * PayService.listAvailableIntegrations.
 */
import { QuickBooksConnectionsDetailsPayload } from './quickbooks'
import { XeroConnectionDetailsPayload } from './xero/types'

/** List of identifiers for each supported third-party integration. */
export enum IntegrationKey {
  /** Xero expenses synchronization integration. */
  XERO_EXPENSES = 'xero_expenses',
  /**
   * QuickBooks expenses synchronization integration.
   * TODO: Need to figure out if this key should be something specific. I don't know
   * */
  QUICKBOOKS = 'quickbooks',
}

/** Activation status of an integration for a specific org. */
export enum IntegrationStatus {
  /** Integration is configured/connected for the organization. */
  ACTIVE = 'ACTIVE',
  /** Integration is available but not configured/connected for the organization. */
  INACTIVE = 'INACTIVE',
}

/** API payload for one integration catalog item. */
export type IntegrationPayload = {
  /** Stable machine-readable integration identifier. */
  key: IntegrationKey
  /** Human-readable integration name for UI display. */
  name: string
  /** Current activation status for the organization. */
  status: IntegrationStatus
  /** Temporary integration-specific details until the catalog is generalized for multiple providers. */
  data?: XeroConnectionDetailsPayload | QuickBooksConnectionsDetailsPayload // | add more types here and use a type field enum so the client can handle the different types (for the future)
}

/**
 * Request to activate or deactivate one integration for an organization.
 *
 * Activation is exclusive: an organization runs one integration at a time, so activating one
 * while another still has an active connection is rejected rather than flipping both.
 */
export type ToggleIntegrationRequest = {
  /** Which integration to toggle. */
  key: IntegrationKey
  /** The status to move it to. */
  status: IntegrationStatus
}
